<script>
export default {
  name: "FeatureValuesEdit",
  props: {
    agentId: [Number, String],
    isActive: Boolean,
  },
  data() {
    return {
      isLoading: false,
      groups: [],
    }
  },
  computed: {
    sortedGroups() {
      return [...this.groups].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    },
  },
  watch: {
    isActive(newValue) {
      if (newValue) this.fetchFeatureValues()
    },
  },
  methods: {
    async fetchFeatureValues() {
      this.isLoading = true
      try {
        const { data } = await this.$axios.get(`user/agents/${this.agentId}/feature-values`)
        this.groups = (data?.data || []).filter(item => item.type === "group")
      } catch (e) {
        throw new Error(e)
      } finally {
        this.isLoading = false
      }
    },
    getFeatureValue(feature) {
      const values = feature.feature_values || []
      const active = values.find(item => !item.deleted_at) || values[0]
      return active?.value ?? ""
    },
    buildValuesMap(items, map = {}) {
      for (const item of items) {
        if (item.type !== "group") map[item.id] = this.getFeatureValue(item)
        const nested = [...(item.group_items || []), ...(item.children || [])]
        if (nested.length) this.buildValuesMap(nested, map)
      }
      return map
    },
    isFeatureVisible(feature, valuesMap) {
      if (!feature.parent_id) return true
      if (!feature.show_on_value) return true
      return valuesMap[feature.parent_id] === feature.show_on_value
    },
    collectGroupRows(items, valuesMap, level = 0) {
      const rows = []
      const sorted = [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      for (const item of sorted) {
        if (item.type === "group") continue
        if (!this.isFeatureVisible(item, valuesMap)) continue
        rows.push({
          ...item,
          _level: level,
          _value: this.getFeatureValue(item),
        })
        if (item.children?.length) {
          rows.push(...this.collectGroupRows(item.children, valuesMap, level + 1))
        }
      }
      return rows
    },
    getGroupRows(group) {
      const items = group.group_items || []
      const valuesMap = this.buildValuesMap(items)
      return this.collectGroupRows(items, valuesMap)
    },
    selectOptions(row) {
      return Array.isArray(row.body) ? row.body : []
    },
  },
}
</script>

<template>
  <div class="has-background-white p-5 is-relative" style="min-height: 100px">
    <b-loading v-model="isLoading" :is-full-page="false" />
    <h2 class="is-size-5 has-text-weight-semibold mb-5">Feature values</h2>

    <p v-if="!isLoading && !sortedGroups.length" class="has-text-grey">Нет данных</p>

    <div v-for="group in sortedGroups" :key="group.id" class="feature-group p-4 mb-4">
      <h3 class="is-size-6 has-text-weight-semibold mb-4">{{ group.description || group.key }}</h3>
      <div class="columns is-multiline">
        <div
          v-for="row in getGroupRows(group)"
          :key="row.id"
          class="column"
          :class="row._level > 0 ? 'is-full' : 'is-half'"
        >
          <b-field
            :label="row.description || row.key"
            :message="row.example || undefined"
            :style="row._level > 0 ? { paddingLeft: `${row._level * 16}px` } : null"
          >
            <b-select
              v-if="row.type === 'select'"
              :value="row._value"
              expanded
              disabled
            >
              <option value="" disabled>—</option>
              <option
                v-for="option in selectOptions(row)"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </b-select>
            <b-input
              v-else-if="row.type === 'number'"
              :value="row._value"
              type="number"
              readonly
            />
            <b-input
              v-else
              :value="row._value"
              readonly
            />
          </b-field>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.feature-group {
  border: 1px solid #dbdbdb;
  border-radius: 8px;
}
</style>
